import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Skeleton,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ContentCut,
  ErrorOutline,
  Fullscreen,
  MyLocation,
  Replay,
  RestartAlt,
  WarningAmber
} from '@mui/icons-material';
import TrimmedVideo from 'components/play/recovery/TrimmedVideo';
import VideoPreviewModal from 'components/play/admin/VideoPreviewModal';
import { readVideoDurationMs } from 'components/play/admin/mediaLimits';

const EASE = 'cubic-bezier(0.32,0.72,0,1)';

// Paso del slider. El recorte tiene que ser sub-segundo (la entrenadora corta "justo antes de
// que entre al cuadro"), pero un paso de 1 ms haría eterno moverse con el teclado: 50 ms es
// poco más de un fotograma y el ajuste exacto sigue estando en los campos de texto.
const STEP_MS = 50;

// Fragmento mínimo. Por debajo de esto no se alcanza a ver nada, el loop del preview queda en
// un parpadeo y los dos thumbs del slider se montan uno encima del otro.
const MIN_CLIP_MS = 300;

// Techo cuando NO conocemos la duración: sin él no habría con qué acotar lo que se escribe a
// mano. Una hora es más de lo que dura cualquier práctica y evita valores absurdos.
const UNKNOWN_CEILING_MS = 3600000;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Milisegundos a `m:ss.mmm`. Se TRUNCA (no se redondea) para que el rótulo coincida con el
 * reloj del reproductor: si el corte va en 18.999 s, el video muestra 0:18 y un rótulo que
 * dijera 0:19 haría creer que la marca quedó movida.
 */
const msToLabel = (ms) => {
  const safe = Math.max(0, Math.trunc(Number(ms) || 0));
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const millis = safe % 1000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
};

/**
 * `m:ss.mmm` a milisegundos, o null si el texto no se entiende (quien llama lo descarta).
 * Se tolera a propósito todo lo que una persona escribe de verdad: "1:05" sin milisegundos,
 * "1:05.4" con uno solo (que vale 400 ms, no 4), "12" como segundos sueltos y la coma decimal
 * del teclado latino. Exigir el formato completo convertiría el campo en una trampa.
 */
const labelToMs = (text) => {
  const raw = String(text ?? '').trim().replace(',', '.');
  if (!raw) return null;

  const match = /^(?:(\d+):)?(\d{1,3})(?:\.(\d{1,3}))?$/.exec(raw);
  if (!match) return null;

  const minutes = Number(match[1] || 0);
  const seconds = Number(match[2]);
  const millis = Number((match[3] || '0').padEnd(3, '0'));
  return minutes * 60000 + seconds * 1000 + millis;
};

const previewShellSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: '16 / 9',
  minHeight: 150,
  maxHeight: 260,
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: 'surface.content',
  border: '1px solid',
  borderColor: 'hairline.main'
};

// Identidad estable: si se recreara en cada render, el <video> recibiría un `sx` nuevo por
// segundo (el playhead re-renderiza) y podría reiniciar la reproducción.
const videoSx = { position: 'absolute', inset: 0, width: '100%', height: '100%' };

const overlaySx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 0.75,
  px: 2
};

const playheadSx = {
  position: 'absolute',
  left: 8,
  bottom: 8,
  px: 1,
  py: 0.25,
  borderRadius: 999,
  backgroundColor: 'rgba(10, 10, 10, 0.68)',
  border: '1px solid',
  borderColor: 'hairline.strong',
  fontVariantNumeric: 'tabular-nums',
  pointerEvents: 'none'
};

// Acciones secundarias (ver completo, quitar recorte, reintentar): sin fondo, el dorado solo
// aparece al pasar por encima para no competir con el CTA de guardar del formulario padre.
const ghostButtonSx = {
  flex: 'none',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 2,
  px: 1.25,
  color: 'text.secondary',
  transition: `color 200ms ${EASE}, background-color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { color: 'gold.main', backgroundColor: 'surface.overlay' },
  '&:active': { transform: 'scale(0.98)' }
};

const sliderSx = (theme) => ({
  color: 'gold.main',
  py: 1.25,
  '& .MuiSlider-rail': { opacity: 1, backgroundColor: theme.palette.hairline.strong },
  '& .MuiSlider-mark': { width: 2, height: 10, backgroundColor: theme.palette.hairline.strong },
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    transition: `box-shadow 200ms ${EASE}, transform 200ms ${EASE}`,
    '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 8px ${theme.palette.gold.soft}` },
    '&.Mui-active': { transform: 'scale(1.12)', boxShadow: `0 0 0 12px ${theme.palette.gold.soft}` }
  },
  '& .MuiSlider-valueLabel': {
    backgroundColor: theme.palette.surface.raised,
    border: '1px solid',
    borderColor: theme.palette.hairline.strong,
    borderRadius: '8px',
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  }
});

/**
 * OTRIX Play (admin) — recortador de video SIN reprocesar el archivo: marca de dónde a dónde
 * se ve, si suena, y (solo en el video introductorio) desde qué punto el cliente ya puede
 * pulsar "empezar". Todo son milisegundos guardados en la fila; el archivo de S3 no se toca.
 *
 * Existe porque el video que graba la entrenadora con el móvil trae ocho segundos de ella
 * acomodando la cámara al principio y otros tantos al final: sin esto, eso es lo primero que
 * ve el cliente. Volver a subir un archivo editado no es opción (200 MB desde un teléfono).
 */
function VideoTrimField({
  videoUrl,
  startMs = null,
  endMs = null,
  durationMs = null,
  hasAudio = false,
  readyMs = null,
  showReadyMark = false,
  onChange,
  label = null
}) {
  const { t } = useTranslation(['play']);

  const [measuredMs, setMeasuredMs] = useState(0);
  const [measuring, setMeasuring] = useState(false);
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  // Cambiar esta cuenta remonta el preview: un fallo de red se reintenta sin salir del editor.
  const [attempt, setAttempt] = useState(0);
  const [fullOpen, setFullOpen] = useState(false);
  // Borrador de los campos de texto: solo existe la clave que se está escribiendo. Al perder
  // el foco se borra, así que un texto inválido revierte solo al valor vigente.
  const [draft, setDraft] = useState({});
  const [dragRange, setDragRange] = useState(null);
  const [dragReady, setDragReady] = useState(null);
  const [playheadMs, setPlayheadMs] = useState(0);

  const playheadRef = useRef(0);
  const activeThumbRef = useRef(1);
  // `onChange` del padre suele ser una lambda nueva en cada render; guardarlo en un ref evita
  // que el efecto que mide la duración se vuelva a disparar (y a descargar metadatos) por eso.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    setFailed(false);
    setBlocked(false);
    setPlayheadMs(0);
    playheadRef.current = 0;
  }, [videoUrl]);

  // La duración se guarda en la fila: solo se mide cuando el padre todavía no la tiene (video
  // subido antes de que existiera el recortador) o cuando cambia el archivo.
  useEffect(() => {
    setMeasuredMs(0);
    if (!videoUrl || (typeof durationMs === 'number' && durationMs > 0)) {
      setMeasuring(false);
      return undefined;
    }

    let alive = true;
    setMeasuring(true);
    readVideoDurationMs(videoUrl).then((ms) => {
      if (!alive) return;
      setMeasuring(false);
      setMeasuredMs(ms);
      // Se le avisa al padre para que quede PERSISTIDA: si no, cada apertura del editor
      // vuelve a descargar los metadatos del video.
      if (ms > 0) onChangeRef.current({ durationMs: Math.round(ms) });
    });

    return () => {
      alive = false;
    };
  }, [videoUrl, durationMs]);

  const total = (typeof durationMs === 'number' && durationMs > 0 ? durationMs : measuredMs) || 0;
  const knowsDuration = total > 0;
  const ceiling = knowsDuration ? total : UNKNOWN_CEILING_MS;
  // En un video más corto que el mínimo teórico, el mínimo pasa a ser la mitad: es preferible
  // un fragmento diminuto a un slider que no deja mover nada.
  const minClip = knowsDuration ? Math.min(MIN_CLIP_MS, Math.floor(total / 2)) : MIN_CLIP_MS;

  // null en las marcas = comportamiento de hoy (video entero), así que el valor efectivo del
  // inicio es 0 y el del fin es el final del archivo. El fin queda "sin definir" solo cuando
  // tampoco pudimos leer la duración.
  const effectiveStart = clamp(typeof startMs === 'number' ? startMs : 0, 0, ceiling);
  const effectiveEnd = (() => {
    if (typeof endMs === 'number') return clamp(endMs, 0, ceiling);
    return knowsDuration ? total : null;
  })();
  const upperEnd = effectiveEnd === null ? ceiling : effectiveEnd;
  const isTrimmed = typeof startMs === 'number' || typeof endMs === 'number' || typeof readyMs === 'number';

  const clipMs = Math.max(0, upperEnd - effectiveStart);

  // Un único camino para escribir marcas: campos de texto, slider y botones "marcar aquí"
  // pasan por aquí, así que las cotas no pueden quedar distintas entre unos y otros.
  const applyValue = (field, value) => {
    if (value === null) {
      onChange({ [`${field}Ms`]: null });
      return;
    }

    const rounded = Math.round(value);
    if (field === 'start') {
      onChange({ startMs: clamp(rounded, 0, Math.max(0, upperEnd - minClip)) });
      return;
    }
    if (field === 'end') {
      onChange({ endMs: clamp(rounded, Math.min(effectiveStart + minClip, ceiling), ceiling) });
      return;
    }
    onChange({ readyMs: clamp(rounded, effectiveStart, upperEnd) });
  };

  const commitText = (field) => {
    const typed = draft[field];
    setDraft((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });

    if (typed === undefined) return;
    // Campo vacío = quitar la marca (vuelve al comportamiento por defecto). Es la única forma
    // de decir "hasta el final" sin conocer el número exacto.
    if (!String(typed).trim()) {
      applyValue(field, null);
      return;
    }

    const parsed = labelToMs(typed);
    if (parsed === null) return; // texto que no se entiende: se ignora y el campo ya revirtió
    applyValue(field, parsed);
  };

  const handleProgress = useCallback((currentMs) => {
    // `currentMs` es el instante ABSOLUTO dentro del archivo (no relativo al recorte): es lo
    // que necesitan los botones "marcar aquí" para escribir una marca comparable con el resto.
    playheadRef.current = currentMs;
    setPlayheadMs(currentMs);
  }, []);

  const handleError = useCallback(() => setFailed(true), []);
  const handleBlocked = useCallback(() => setBlocked(true), []);

  const markHere = (field) => applyValue(field, playheadRef.current || 0);

  const boundRange = ([rawStart, rawEnd], thumb) => {
    // `disableSwap` impide que los thumbs se crucen, pero NO que se junten: sin esto se puede
    // dejar un fragmento de 0 ms arrastrando uno contra el otro.
    if (thumb === 0) return [Math.min(rawStart, Math.max(0, rawEnd - minClip)), rawEnd];
    return [rawStart, Math.max(rawEnd, Math.min(total, rawStart + minClip))];
  };

  const resetTrim = () =>
    onChange({ startMs: null, endMs: null, ...(showReadyMark ? { readyMs: null } : {}) });

  const sliderStart = dragRange ? dragRange[0] : effectiveStart;
  const sliderEnd = dragRange ? dragRange[1] : upperEnd;
  const readyValue = dragReady !== null ? dragReady : clamp(readyMs ?? effectiveStart, 0, ceiling);

  // Mientras se escribe manda el borrador; ya confirmado, manda el valor. Una marca en null se
  // muestra VACÍA (no como "0:00.000"): vacío significa "sin recorte", y un cero explícito
  // diría que el corte empieza justo en el primer milisegundo, que no es lo mismo.
  const fieldText = (field, value) => {
    if (draft[field] !== undefined) return draft[field];
    return value === null ? '' : msToLabel(value);
  };

  const renderTimeField = (field, value, fieldLabel, placeholder, markHint) => (
    <TextField
      size="small"
      label={fieldLabel}
      placeholder={placeholder}
      value={fieldText(field, value)}
      onChange={(e) => setDraft((current) => ({ ...current, [field]: e.target.value }))}
      onBlur={() => commitText(field)}
      onKeyDown={(e) => {
        // Enter confirma sin tener que salir del campo con el mouse.
        if (e.key === 'Enter') {
          e.preventDefault();
          e.target.blur();
        }
      }}
      InputProps={{
        sx: { fontVariantNumeric: 'tabular-nums' },
        // Anidado dentro de `InputProps` y no como `inputProps` suelto: eslint compara los
        // nombres de props SIN distinguir mayúsculas y los tomaba por duplicados.
        inputProps: { inputMode: 'decimal', 'aria-label': fieldLabel },
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={markHint}>
              <IconButton
                size="small"
                edge="end"
                onClick={() => markHere(field)}
                aria-label={markHint}
                sx={{
                  color: 'text.disabled',
                  transition: `color 200ms ${EASE}, transform 200ms ${EASE}`,
                  '&:hover': { color: 'gold.main' },
                  '&:active': { transform: 'scale(0.98)' }
                }}>
                <MyLocation sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        )
      }}
      sx={{ flex: 1, minWidth: 140 }}
    />
  );

  return (
    <Stack
      spacing={1.5}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.raised'
      }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <ContentCut sx={{ fontSize: 16, color: 'gold.main', flex: 'none' }} />
        <Typography variant="caption" fontWeight={800} sx={{ flex: 1, minWidth: 0 }} noWrap>
          {label || t('recoveryAdmin.trim.title', { ns: 'play', defaultValue: 'Recorte del video' })}
        </Typography>
        <Button
          size="small"
          startIcon={<Fullscreen sx={{ fontSize: 16 }} />}
          onClick={() => setFullOpen(true)}
          sx={ghostButtonSx}>
          {t('recoveryAdmin.trim.viewFull', { ns: 'play', defaultValue: 'Ver completo' })}
        </Button>
      </Stack>

      <Box sx={previewShellSx}>
        {failed && (
          <Box sx={overlaySx}>
            <ErrorOutline sx={{ fontSize: 26, color: 'sessionStatus.absent.fg' }} />
            <Typography variant="caption" fontWeight={800}>
              {t('recoveryAdmin.trim.errorTitle', { ns: 'play', defaultValue: 'No pudimos cargar el video' })}
            </Typography>
            <Button
              size="small"
              startIcon={<Replay sx={{ fontSize: 15 }} />}
              onClick={() => {
                setFailed(false);
                setBlocked(false);
                setAttempt((current) => current + 1);
              }}
              sx={ghostButtonSx}>
              {t('recoveryAdmin.trim.retry', { ns: 'play', defaultValue: 'Reintentar' })}
            </Button>
          </Box>
        )}

        {!failed && (
          <TrimmedVideo
            // Se remonta cuando cambian las marcas YA CONFIRMADAS (no mientras se arrastra):
            // así el preview siempre arranca en el nuevo inicio, que es lo que se quiere ver
            // justo después de moverlo.
            key={`${videoUrl}|${startMs ?? ''}|${endMs ?? ''}|${attempt}`}
            src={videoUrl}
            startMs={startMs}
            endMs={endMs}
            // El preview de la lista SIEMPRE va mudo: abrir el editor no puede ponerse a sonar.
            // El audio se comprueba en "Ver completo", que sí respeta `hasAudio`.
            muted
            loop
            autoPlay
            controls
            objectFit="contain"
            onError={handleError}
            onPlayBlocked={handleBlocked}
            onProgress={handleProgress}
            sx={videoSx}
          />
        )}

        {!failed && (
          <Typography variant="caption" fontWeight={700} sx={playheadSx}>
            {msToLabel(playheadMs)}
          </Typography>
        )}
      </Box>

      {blocked && !failed && (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {t('recoveryAdmin.trim.blocked', {
            ns: 'play',
            defaultValue: 'Toca reproducir para ver el fragmento.'
          })}
        </Typography>
      )}

      {measuring && <Skeleton variant="rounded" height={34} />}

      {!measuring && knowsDuration && (
        <Box sx={{ px: 0.5 }}>
          <Slider
            value={[sliderStart, sliderEnd]}
            onChange={(_, next, thumb) => {
              activeThumbRef.current = thumb;
              setDragRange(boundRange(next, thumb));
            }}
            onChangeCommitted={(_, next) => {
              const bounded = boundRange(next, activeThumbRef.current);
              setDragRange(null);
              onChange({ startMs: Math.round(bounded[0]), endMs: Math.round(bounded[1]) });
            }}
            disableSwap
            min={0}
            max={total}
            step={STEP_MS}
            valueLabelDisplay="auto"
            valueLabelFormat={msToLabel}
            getAriaValueText={msToLabel}
            getAriaLabel={(index) =>
              index === 0
                ? t('recoveryAdmin.trim.startLabel', { ns: 'play', defaultValue: 'Inicio' })
                : t('recoveryAdmin.trim.endLabel', { ns: 'play', defaultValue: 'Fin' })
            }
            sx={sliderSx}
          />
        </Box>
      )}

      {!measuring && !knowsDuration && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <WarningAmber sx={{ fontSize: 16, color: 'text.disabled', flex: 'none', mt: 0.2 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {t('recoveryAdmin.trim.noDuration', {
              ns: 'play',
              defaultValue:
                'No pudimos leer la duración de este video, así que no hay barra para arrastrar. Escribe las marcas a mano o toma el punto con el reproductor.'
            })}
          </Typography>
        </Stack>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
        {renderTimeField(
          'start',
          typeof startMs === 'number' ? startMs : null,
          t('recoveryAdmin.trim.startLabel', { ns: 'play', defaultValue: 'Inicio' }),
          t('recoveryAdmin.trim.startPlaceholder', { ns: 'play', defaultValue: 'Desde el comienzo' }),
          t('recoveryAdmin.trim.markStart', { ns: 'play', defaultValue: 'Marcar el inicio aquí' })
        )}
        {renderTimeField(
          'end',
          typeof endMs === 'number' ? endMs : null,
          t('recoveryAdmin.trim.endLabel', { ns: 'play', defaultValue: 'Fin' }),
          t('recoveryAdmin.trim.endPlaceholder', { ns: 'play', defaultValue: 'Hasta el final' }),
          t('recoveryAdmin.trim.markEnd', { ns: 'play', defaultValue: 'Marcar el fin aquí' })
        )}
      </Stack>

      {/* La marca de "listo" va en su PROPIO slider, nunca como tercer thumb del de arriba:
          MUI reordena internamente el array de valores y se pierde cuál era cuál. */}
      {showReadyMark && (
        <Stack spacing={1} sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'hairline.main' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, pt: 0.75 }}>
            {t('recoveryAdmin.trim.readyHint', {
              ns: 'play',
              defaultValue: 'Desde este punto el cliente ya puede pulsar empezar.'
            })}
          </Typography>

          {!measuring && knowsDuration && (
            <Box sx={{ px: 0.5 }}>
              <Slider
                value={readyValue}
                onChange={(_, next) => setDragReady(Array.isArray(next) ? next[0] : next)}
                onChangeCommitted={(_, next) => {
                  setDragReady(null);
                  applyValue('ready', Array.isArray(next) ? next[0] : next);
                }}
                min={0}
                max={total}
                step={STEP_MS}
                // Sin track relleno: esto es un PUNTO, no un rango; las marcas recuerdan dónde
                // quedaron el inicio y el fin del recorte, que es contra lo que se compara.
                track={false}
                marks={[{ value: effectiveStart }, { value: upperEnd }]}
                valueLabelDisplay="auto"
                valueLabelFormat={msToLabel}
                getAriaValueText={msToLabel}
                aria-label={t('recoveryAdmin.trim.readyLabel', {
                  ns: 'play',
                  defaultValue: 'Listo para empezar'
                })}
                sx={sliderSx}
              />
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            {renderTimeField(
              'ready',
              typeof readyMs === 'number' ? readyMs : null,
              t('recoveryAdmin.trim.readyLabel', { ns: 'play', defaultValue: 'Listo para empezar' }),
              t('recoveryAdmin.trim.readyPlaceholder', { ns: 'play', defaultValue: 'Desde el comienzo' }),
              t('recoveryAdmin.trim.markReady', { ns: 'play', defaultValue: 'Marcar el punto de listo aquí' })
            )}
            <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          </Stack>
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'hairline.main' }}>
        {/* El switch solo HABILITA el control de sonido: todos los navegadores arrancan el
            autoplay en mudo, así que esto no desmutea nada por sí solo. Lo que hace es
            autorizar al reproductor a ofrecer el audio cuando el cliente lo pida. */}
        <Switch
          size="small"
          checked={Boolean(hasAudio)}
          onChange={(e) => onChange({ hasAudio: e.target.checked })}
          inputProps={{
            'aria-label': t('recoveryAdmin.trim.audio', {
              ns: 'play',
              defaultValue: 'Este video tiene audio'
            })
          }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" fontWeight={700}>
            {t('recoveryAdmin.trim.audio', { ns: 'play', defaultValue: 'Este video tiene audio' })}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.4 }}>
            {t('recoveryAdmin.trim.audioHint', {
              ns: 'play',
              defaultValue: 'Si lo dejas apagado, el video se reproduce siempre en silencio.'
            })}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
          {knowsDuration
            ? t('recoveryAdmin.trim.summary', {
                ns: 'play',
                clip: msToLabel(clipMs),
                total: msToLabel(total),
                defaultValue: 'Se verán {{clip}} de {{total}}'
              })
            : t('recoveryAdmin.trim.format', {
                ns: 'play',
                defaultValue: 'Formato m:ss.mmm, por ejemplo 1:05.400'
              })}
        </Typography>
        {isTrimmed && (
          <Button
            size="small"
            startIcon={<RestartAlt sx={{ fontSize: 16 }} />}
            onClick={resetTrim}
            sx={ghostButtonSx}>
            {t('recoveryAdmin.trim.reset', { ns: 'play', defaultValue: 'Quitar recorte' })}
          </Button>
        )}
      </Stack>

      {/* El original, sin marcas: comparar el recorte contra lo que se grabó es exactamente lo
          que hace falta para decidir dónde cortar. */}
      <VideoPreviewModal
        open={fullOpen}
        src={videoUrl}
        startMs={null}
        endMs={null}
        hasAudio={Boolean(hasAudio)}
        title={t('recoveryAdmin.trim.fullTitle', { ns: 'play', defaultValue: 'Video original completo' })}
        onClose={() => setFullOpen(false)}
      />
    </Stack>
  );
}

VideoTrimField.propTypes = {
  videoUrl: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // eslint-disable-next-line react/require-default-props
  startMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  endMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  durationMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  hasAudio: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  readyMs: PropTypes.number,
  // eslint-disable-next-line react/require-default-props
  showReadyMark: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  label: PropTypes.string
};

export default VideoTrimField;
